import _toConsumableArray from "/app/scripts/smweb-scripts/node_modules/@babel/runtime/helpers/toConsumableArray";
import React, { useMemo } from 'react';
import { QuestionRowLayout } from '@sm/question-ui/respondent-survey';
import { selectQuestionTypeDefinition, isExternalQuestionWithTypeId } from '@sm/question-definitions';
import { u as useProviderAccessContext, a as useExternalQuestionAugmentation, c as createVisualization } from './utils-02b1d07c.js';

var UnsupportedQuestionTypeError = function UnsupportedQuestionTypeError(_ref) {
  var question = _ref.question;
  var typeId = isExternalQuestionWithTypeId(question) && question.typeId;
  return React.createElement("div", null, "Unsupported Question Type: ", typeId);
};

var SingleQuestionController = function SingleQuestionController(_ref2) {
  var externalQuestion = _ref2.question,
      displayOptions = _ref2.displayOptions,
      namedSlots = _ref2.namedSlots,
      responseSet = _ref2.responseSet;

  var _useProviderAccessCon = useProviderAccessContext(),
      useUpstreamConfig = _useProviderAccessCon.useUpstreamConfig,
      useVisualizationList = _useProviderAccessCon.useVisualizationList;

  var _useUpstreamConfig = useUpstreamConfig(),
      capability = _useUpstreamConfig.capability,
      upstreamActions = _useUpstreamConfig.upstreamActions,
      renderProps = _useUpstreamConfig.renderProps,
      settings = _useUpstreamConfig.settings;
  /* NOTE:
  Since Controllers are expected to be capability-independent -- and should not import
  the CapabilityProvider types -- this config is returning the type params as `unknown`s.
  This shouldn't be a problem -- as the Controller doesn't need to know
  any specifics about these types - other than passing them to the compatible
  Visualizations (something that the Visualization Helpers runtime already takes
  care of). However, the consequence is that overall type safety of the Controller
  is not as strict as we would like it to be. We should probably create a task
  to improve this later.
  */
  // TODO: add controller type to the visualization selection -- to allow for different DisplayOptions and better support for nested visualizations


  var visualizationOptions = useVisualizationList(capability
  /* , controllerType */
  ); // Augment the external question with the type id

  var augmentedExternalQuestion = useExternalQuestionAugmentation(externalQuestion, responseSet);
  var definition = selectQuestionTypeDefinition(augmentedExternalQuestion);

  if (!definition) {
    return React.createElement(UnsupportedQuestionTypeError, {
      question: augmentedExternalQuestion
    });
  }

  var toExternalQuestion = function toExternalQuestion(q) {
    return definition.toExternalQuestion(externalQuestion, q, capability);
  }; // useMemo to optimize this


  var ChosenVisualization = createVisualization(capability, definition.typeId, visualizationOptions, upstreamActions, settings, renderProps);
  /* NOTE:
  Type safety improvement: Currently 'QuestionType' is all the possible question types
  -- while createVisualization will choose a visualization that can render
  question types for a specific capability -- meaning that the resulting question
  type of 'ChosenVisualization' is a subset of all the possible QuestionTypes
  (only those that have viable visualizations for that capability -- and, in the
  future, ControllerType)
  */

  var question = definition.fromExternalQuestion(augmentedExternalQuestion, capability);
  return React.createElement(ChosenVisualization, {
    question: question,
    displayOptions: displayOptions,
    namedSlots: namedSlots,
    toExternalQuestion: toExternalQuestion
  });
};
/**
 * Group questions based each question's layout config
 * @param questions
 * @description
 * - if a question has layout config as new row, it will start a new group
 * - if a question has layout config as right aligned, it will be grouped with the previous group together
 * @example
 * input = [
 *   { layout: { placement: 'NEW_ROW' }},
 *   { layout: { placement: 'RIGHT_ALIGNED' }},
 *   { layout: { placement: 'NEW_ROW' }},
 *   { layout: { placement: 'RIGHT_ALIGNED' }},
 * ]
 *
 * output = [
 *   [ { layout: { placement: 'NEW_ROW' }}, { layout: { placement: 'RIGHT_ALIGNED' }} ],
 *   [ { layout: { placement: 'NEW_ROW' }}, { layout: { placement: 'RIGHT_ALIGNED' }} ]
 * ]
 */


var groupQuestionsWithLayoutConfig = function groupQuestionsWithLayoutConfig(questions) {
  return questions.reduce(function (groups, question) {
    if ('layout' in question && 'placement' in question.layout && question.layout.placement === 'RIGHT_ALIGNED') {
      var preGroup = groups.pop();
      var newGroup = preGroup ? [].concat(_toConsumableArray(preGroup), [question]) : [question];
      groups.push(newGroup);
    } else {
      groups.push([question]);
    }

    return groups;
  }, []);
};

var QuestionListController = function QuestionListController(_ref3) {
  var questions = _ref3.questions;
  var groups = useMemo(function () {
    return groupQuestionsWithLayoutConfig(questions);
  }, [questions]);
  return React.createElement(React.Fragment, null, groups.map(function (group) {
    return React.createElement(QuestionRowLayout, {
      key: "group_".concat(group.map(function (g) {
        return g.id;
      }).concat('-'))
    }, group.map(function (question) {
      return React.createElement(SingleQuestionController, Object.assign({
        key: question.id
      }, {
        question: question
      }));
    }));
  }));
};

export { QuestionListController as Q, SingleQuestionController as S };